import React from 'react';
import {Dialog, Form, Button} from 'components/core';
import {Grid, Divider} from '@material-ui/core';
import { Container, Draggable } from "react-smooth-dnd";
import {TemplateFieldsCtxProvider, TemplateFieldsCtx} from 'components/dataProviders/procedureTemplateFields';
import {makeStyles} from '@material-ui/core/styles';
import {Add, DragHandle} from '@material-ui/icons';
import {Delete} from '@material-ui/icons';
import clsx from 'clsx';

const styles = makeStyles((theme) => ({
    dialog: {
        maxWidth: '80vw',
        height: '90vh'
    },
    dialogContent:{
        padding: 0
    },
    root: {
        display: 'flex',
        flexDirection: 'row',
        height: '100%',
        width: 800
    },
    stepsList: {
        flex: '0 0 200px',
        overflowY: 'auto',
        padding: 10,
        flexShrink: 0,
        '& > *':
        {
            marginBottom: 10
        }
    },
    stepButton: {
        backgroundColor: 'transparent',
        color: theme.palette.text.secondary
    },
    form: {
        flexGrow: 1,
        padding: 25,
        overflowX: 'hidden'
    },
    addButton: {
        backgroundColor: 'transparent',
        color: theme.palette.primary.main
    }
  }), {name: "FieldsDialog"});


export default function EditFieldDialogWrapper({onConfirm, onChange, loading, templateID, skipFieldContext, ...props})
{
    if(!templateID) return null;
    const dialog = <EditFieldsDialog onConfirm={onConfirm} onChange={onChange} loading={loading} templateID={templateID} {...props} />;
    if(skipFieldContext) return dialog;
    else return <TemplateFieldsCtxProvider templateID={templateID}>
                    {dialog}
            </TemplateFieldsCtxProvider>
}

function EditFieldsDialog({onConfirm, onChange, templateID, stepFields = false, ...props})
{
    const classes = styles();
    const [formValues, setFormValues] = React.useState({});
    const ctx = React.useContext(TemplateFieldsCtx);
    const [selectedField, setSelectedField] = React.useState(null);

    const dataSubset = stepFields ? ctx.data.stepFields : ctx.data.procedureFields;
    
    React.useEffect(function InitializeSelectedField(){
        if(!selectedField && dataSubset.length > 0) setSelectedField(dataSubset[0].fieldID);
        if(selectedField)
        {
            if(ctx.data.indexed[selectedField]) setFormValues({...ctx.data.indexed[selectedField]});
            else if(dataSubset.length > 0) setSelectedField(dataSubset[0].fieldID);
        }
    }, [dataSubset, ctx, selectedField]);

    const saveCurrentField = React.useCallback(async () => {
        if(selectedField)
        {
            await ctx.funcs.update(selectedField, formValues);
        }
    }, [selectedField, ctx, formValues])

    const handleConfirm = React.useCallback(() =>
    {
        saveCurrentField().then(() => onConfirm ? onConfirm(formValues) : null);
    }, [onConfirm, saveCurrentField, formValues]);

    function addField()
    {
        saveCurrentField().then(() => {
            ctx.funcs.create({name: "Campo "+(dataSubset.length+1), description: "", dataType: 4, order: dataSubset.length, isStepField: stepFields, placeholder: "", defaultValue: ""}).then((fieldID) => {
                if(fieldID) setSelectedField(fieldID);
            });
        });
    }

    function selectField(fieldID)
    {
        saveCurrentField().then(() => setSelectedField(fieldID));
    }


    // wrap step form in memo to avoid rerendering when changing saved values
    // it was causing problems because the context provider was changing every render and triggering effects reinitializing values
    const stepComps = React.useMemo(() => {

        function handleFormChange(newValues)
        {
            setFormValues({...ctx.data.indexed[selectedField], ...newValues});
            if(onChange) onChange(newValues);
        }
        if(!selectedField) return null;
        return <FieldForm fieldID={selectedField} loading={ctx.loading} onChange={(newValues) => handleFormChange(newValues)}/>
    }, [selectedField, ctx, onChange]);

    const onDrop = ({ removedIndex, addedIndex }) => {
        //setItems(items => arrayMove(items, removedIndex, addedIndex));
        ctx.funcs.update(dataSubset[removedIndex].fieldID, {order: addedIndex+1});
      };

    return <Dialog {...props}
        className={classes.dialog}
        title={ stepFields ? "Campos de los pasos" : "Campos generales del procedimiento"}
        subtitle={"Edita los campos que aparecerán en "+ stepFields ? "los pasos del procedimiento" : "la descripción general del procedimiento"}
        loading={ctx.loading}
        onConfirm={handleConfirm}
        open={props.open}
        onClose={props.onClose}
        confirm={stepFields ? "Completar" : "Siguiente"}
        cancel="Anterior"
        headerDivider
        contentClass={classes.dialogContent}
        >
        <div className={classes.root}>
            <div className={classes.stepsList}>
            <Container dragHandleSelector=".drag-handle" lockAxis="y" onDrop={onDrop}>
                {dataSubset.map((step, index) => <Draggable key={index}>
                    <Button key={index} onClick={()=>selectField(step.fieldID)} className={step.fieldID !== selectedField && classes.stepButton} color="secondary" startIcon={<DragHandle className="drag-handle" />}>Campo {index+1}</Button>
                </Draggable>)}
            </Container>
                <Button className={classes.addButton} color="secondary" onClick={addField}><Add />Añadir campo</Button>
            </div>
            <Divider orientation="vertical" />
            <div className={classes.form}>
                {stepComps}
            </div>
        </div>
    </Dialog>
}

function FieldForm({fieldID, onChange=()=>{}, ...props})
{
    const fieldsCtx = React.useContext(TemplateFieldsCtx);
    const field = fieldsCtx.data.indexed[fieldID];
    let defaultValues = {name: '', description: '', defaultValue: '', placeholder: '', dataType: 4, order: 1};
    if(field) defaultValues = {name: field.name, description: field.description, defaultValue: field.defaultValue, placeholder: field.placeholder, dataType: field.dataType.typeID, order: field.order};

    const [values, setValues] = React.useState(defaultValues);


    React.useEffect(function InitializeValues() {
        if(field) setValues({name: field.name, description: field.description, defaultValue: field.defaultValue, order: field.order, dataType: field.dataType.typeID});
    }, [field]);

    const handleFormChange = (newValues) => {
        let tmpValues = {...values, ...newValues};
        setValues(tmpValues);
        onChange(newValues);
    }

    const deleteField = () => {
        fieldsCtx.funcs.delete(fieldID);
    }

    
    if(!field) return null;
    const dataTypeItems = [
        // {value: 1, content: "Si / No"},
        // {value: 2, content: "Número entero"},
        // {value: 3, content: "Número decimal"},
        {value: 4, content: "Texto"},
        {value: 5, content: "Imagen"},
        {value: 6, content: "Video"},
        {value: 7, content: "Archivo"}
    ]

    let fields = [
        {
            type: "text",
            label: "Nombre",
            id: "name",
            placeholder: "Nombre del campo",
            mandatory: true,
            value: values.name
        },
        {
            type: "selector",
            label: "Tipo",
            id: "dataType",
            placeholder: "Tipo de contenido",
            mandatory: true,
            value: values.dataType,
            items: dataTypeItems
        },
        {
            type: "textarea",
            label: "Descripción",
            id: "description",
            placeholder: "",
            mandatory: false,
            value: values.description
        }
    ];

    if(values.dataType <= 4)
    {
        fields.push(
            {
                type: "text",
                label: "Texto explicativo",
                id: "placeholder",
                placeholder: "El texto que aparecerá en gris si el campo está vacío, como éste",
                mandatory: false,
                value: values.placeholder
            },
            {
                type: "text",
                label: "Valor por defecto",
                id: "defaultValue",
                placeholder: "El valor del campo si no se rellena",
                mandatory: false,
                value: values.defaultValue
            }
        );
    }

    fields.push(
        {
            type: "custom",
            id: "delete",
            content: <><Button fullWidth={false} color="error" onClick={()=>deleteField()}><Delete />Eliminar campo</Button></>
        }
    );

    return <Form onChange={(newValues) => handleFormChange(newValues)} fieldErrors={[]} fields={fields} values={values}/>
}